import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { formatDistanceToNow } from 'date-fns';

const WorkHistorySection = ({ workerId, totalJobsCompleted }) => {
  const [workHistory, setWorkHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkHistory();
  }, [workerId]);

  const fetchWorkHistory = async () => {
    try {
      setLoading(true);
      
      // Fetch completed applications with job details and ratings
      const { data, error } = await supabase
        ?.from('applications')
        ?.select(`
          id,
          created_at,
          job:jobs(
            id,
            title,
            description,
            category,
            budget_min,
            budget_max,
            ratings(
              rating,
              review
            )
          )
        `)
        ?.eq('worker_id', workerId)
        ?.eq('status', 'completed')
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;

      setWorkHistory(data || []);
    } catch (err) {
      console.error('Error fetching work history:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Briefcase" className="w-5 h-5 text-primary" />
          Work History
        </h2>
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Icon name="Briefcase" className="w-5 h-5 text-primary" />
          Work History
        </h2>
        <span className="text-sm font-medium text-muted-foreground">
          {totalJobsCompleted} jobs completed
        </span>
      </div>

      {workHistory?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Briefcase" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No completed jobs yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {workHistory?.map((item) => {
            const job = item?.job;
            const rating = item?.job?.ratings?.[0];

            return (
              <div 
                key={item?.id}
                className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      {job?.title || 'Untitled Job'}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      {job?.category || 'General'}
                    </p>
                  </div>
                  {rating && (
                    <div className="flex items-center gap-1 bg-warning/10 px-2 py-1 rounded">
                      <Icon name="Star" className="w-4 h-4 text-warning fill-warning" />
                      <span className="text-sm font-semibold text-foreground">
                        {rating?.rating?.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                {job?.description && (
                  <p className="text-sm text-foreground mb-3 line-clamp-2">
                    {job?.description}
                  </p>
                )}

                {rating?.review && (
                  <div className="bg-background rounded-lg p-3 mb-3">
                    <p className="text-sm text-muted-foreground italic">
                      "{rating?.review}"
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    Completed {formatDistanceToNow(new Date(item?.created_at), { addSuffix: true })}
                  </span>
                  {job?.budget_min && job?.budget_max && (
                    <span className="font-medium text-foreground">
                      ₱{job?.budget_min} - ₱{job?.budget_max}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkHistorySection;